import * as Sentry from "@sentry/react";
import type { Span, SpanAttributes } from "@sentry/core";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";
import { armStuckQueryTimers } from "#stuck-query-watchdog";
import { mirrorWatchdogReportToDevConsole } from "#dev-watchdog-sink";

/**
 * Performance-tracing helpers (web). Mirror of `perf.native.tsx` backed by
 * `@sentry/react`. Same surface so app code imports one `./perf` path that
 * resolves per platform. See the native file for the full rationale.
 *
 * No-ops unless tracing is sampled (see `sentry-setup.web` — `tracesSampleRate`).
 */

export const PERF_OP = {
  db: "han.db",
  query: "han.query",
  suspense: "han.suspense",
} as const;

/**
 * Every app perf span is a CHILD span or nothing. A span started with no active parent becomes a
 * root span, and Sentry ships each root span as its own transaction: event processors, scope and
 * breadcrumb merge, serialization, and (on native) a base64 envelope across the bridge — all on
 * the JS thread. `instrumentQueryCachePerf` opens one span per query fetch, and a card rating
 * refetches a few dozen queries, so with no screen transaction open a single rating emitted 30+
 * transactions inside a second (seen in a staging diagnostic report) at exactly the moment the
 * deck needs the JS thread to promote the next card. Under a screen transaction the spans still
 * nest and cost one envelope in total; outside one they are skipped.
 */
const CHILD_SPAN_ONLY = { onlyIfParent: true } as const;

export function startPerfSpan<T>(
  name: string,
  options: { attributes?: SpanAttributes; op?: string },
  fn: (span: Span) => T,
): T {
  return Sentry.startSpan({ attributes: options.attributes, name, op: options.op ?? "han.op" }, fn);
}

export function recordPerfDuration(
  name: string,
  options: { attributes?: SpanAttributes; op?: string },
  durationMs: number,
  endedAtMs: number = Date.now(),
): void {
  const span = Sentry.startInactiveSpan({
    ...CHILD_SPAN_ONLY,
    attributes: options.attributes,
    name,
    op: options.op ?? "han.op",
    startTime: new Date(endedAtMs - durationMs),
  });
  span.end(new Date(endedAtMs));
}

export function instrumentSuspenseWait<T>(
  queryKeyLabel: string,
  attributes: SpanAttributes,
  promise: Promise<T>,
): Promise<T> {
  const span = Sentry.startInactiveSpan({
    ...CHILD_SPAN_ONLY,
    attributes,
    name: `suspend ${queryKeyLabel}`,
    op: PERF_OP.suspense,
  });
  promise.then(
    () => span.end(),
    () => {
      span.setStatus({ code: 2, message: "suspense_rejected" });
      span.end();
    },
  );
  return promise;
}

interface QueryCachePerfEvent {
  query: {
    meta?: Record<string, unknown> | undefined;
    queryHash: string;
    queryKey: readonly unknown[];
    state: { fetchStatus?: string; status: string };
  };
  type: string;
}

interface SubscribableQueryCache {
  subscribe: (listener: (event: QueryCachePerfEvent) => void) => () => void;
}

/**
 * Bridge for the stuck-query watchdog: it must not import `captureSentryException`
 * (that lives in `sentry-setup.*`, which would close an import cycle), so each perf
 * variant hands it the platform SDK's capture directly.
 */
function captureStuckQuery(error: Error, options: Record<string, unknown>): void {
  // This bridge bypasses `captureSentryException`, so it needs its own dev mirror — otherwise the
  // stuck-query watchdog stays silent in dev while the other three are audible.
  mirrorWatchdogReportToDevConsole(error);
  Sentry.captureException(error, options);
}

function queryKeyLabel(queryKey: readonly unknown[]): string {
  const head: string[] = [];
  for (const segment of queryKey) {
    if (typeof segment === "string") {
      head.push(segment);
    } else {
      break;
    }
  }
  return isNonEmpty(head) ? head.join(":") : "query";
}

export function instrumentQueryCachePerf(queryCache: SubscribableQueryCache): () => void {
  const openSpans = new Map<string, Span>();
  // Stuck-fetch watchdog timers, keyed the same way. Same two edges as the spans
  // (enter `fetching` → arm, settle/evict → disarm), so it rides this one
  // subscription instead of a second per-component mechanism.
  const stuckTimers = new Map<string, () => void>();

  return queryCache.subscribe((event) => {
    const { query } = event;

    // Close a still-open span if its query is evicted mid-fetch (see native variant).
    if (event.type === "removed") {
      const leaked = openSpans.get(query.queryHash);
      if (leaked !== undefined) {
        leaked.setAttribute("han.query.result", "removed");
        leaked.end();
        openSpans.delete(query.queryHash);
      }
      // An evicted query is not stuck — it is gone. Disarm so a cache GC during a
      // slow fetch can't report a hang that no longer has anyone waiting on it.
      stuckTimers.get(query.queryHash)?.();
      stuckTimers.delete(query.queryHash);
      return;
    }

    if (event.type !== "updated") {
      return;
    }
    if (query.meta?.perfIgnore === true) {
      return;
    }

    const isFetching = query.state.fetchStatus === "fetching";
    const existing = openSpans.get(query.queryHash);

    if (isFetching && existing === undefined) {
      const label = queryKeyLabel(query.queryKey);
      const span = Sentry.startInactiveSpan({
        ...CHILD_SPAN_ONLY,
        attributes: {
          "han.query.key": label,
        },
        name: `query ${label}`,
        op: PERF_OP.query,
      });
      openSpans.set(query.queryHash, span);
      // `status === "success"` here means data is already cached, so this fetch is a
      // BACKGROUND refetch: Suspense keeps the old data on screen and no boundary
      // re-suspends, which is why this case has no other instrument.
      const isBackgroundRefetch = query.state.status === "success";
      stuckTimers.set(
        query.queryHash,
        armStuckQueryTimers(captureStuckQuery, (elapsedMs) => ({
          elapsedMs,
          isBackgroundRefetch,
          queryHash: query.queryHash,
          queryLabel: label,
        })),
      );
      return;
    }

    if (!isFetching && existing !== undefined) {
      existing.setAttribute("han.query.result", query.state.status);
      if (query.state.status === "error") {
        existing.setStatus({ code: 2, message: "query_error" });
      }
      existing.end();
      openSpans.delete(query.queryHash);
      stuckTimers.get(query.queryHash)?.();
      stuckTimers.delete(query.queryHash);
    }
  });
}

export const getActiveSpan = Sentry.getActiveSpan;
